
import React from "react";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OrganizationWithRelations } from "@/types/custom-types";

interface ContactsTabProps {
  organization: OrganizationWithRelations;
  id: string;
}

export function ContactsTab({ organization, id }: ContactsTabProps) {
  const contacts = organization.contacts || [];
  const contactCount = contacts.length;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Contacts ({contactCount})</CardTitle>
          <CardDescription>
            People associated with {organization.name}
          </CardDescription>
        </div>
        <Button asChild size="sm">
          <Link to={`/contacts/create?organization=${id}`}>
            <Users className="mr-2 h-4 w-4" />
            Add Contact
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {contactCount === 0 ? (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
            <h3 className="mt-2 text-lg font-medium">No contacts yet</h3>
            <p className="mt-1 text-muted-foreground">
              Get started by adding your first contact for this organization
            </p>
            <Button asChild className="mt-4">
              <Link to={`/contacts/create?organization=${id}`}>
                Add Contact
              </Link>
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {contacts.map(contact => (
              <div key={contact.id} className="py-3 flex justify-between items-center">
                <div>
                  <Link 
                    to={`/contacts/${contact.id}`}
                    className="font-medium hover:underline"
                  >
                    {contact.first_name} {contact.last_name}
                  </Link>
                  <div className="text-sm text-muted-foreground">
                    {contact.job_title || "No title"}
                    {contact.email && ` • ${contact.email}`}
                  </div>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <Link to={`/contacts/${contact.id}`}>
                    View
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
